/**
 * Account portal iframe helpers — order ID swapping, external login, and DOM observation.
 *
 * Moved from templates/components/scripts/account-iframe.html (Phase 3 refactor).
 * Handlebars dependencies are read at call-time from window.BC_CONTEXT which is
 * populated by init-globals.html before the webpack bundle loads.
 *
 * Call initAccountIframe() from global.js onReady().
 */

import { parseBoolean } from '../../f/atc-analytics';
import { isValidLoginPassword } from '../../common/utils/review-utils';

function modifyFormElements(iframeDocument) {
    const orderLinesByCreatedOnDate = iframeDocument.querySelectorAll('[data-testid="tableBody-createdAt"]');
    orderLinesByCreatedOnDate.forEach(tableData => {
        if (tableData && tableData.dataset.testid === 'tableBody-createdAt') {
            getOrderInfo(tableData.parentElement);
        }
    });

    const forms = iframeDocument.querySelectorAll('[data-testid="tableBody-orderId"]');
    forms.forEach(input => {
        if (input && input.dataset.testid === 'tableBody-orderId') {
            const content = input.textContent.trim();
            if (!isNaN(content) && content !== '') {
                swapOrderId(input);
            }
        }
    });
}

function modifyMobileOrderElements(iframeDocument) {
    const forms = iframeDocument.querySelectorAll("div.between-flex.css-1yoxm4m > div.MuiBox-root.css-70qvj9 > h5");
    forms.forEach(input => {
        if (input) {
            input.style.display = 'none';
            let orderId = input.textContent.trim().substring(1, input.textContent.length);
            if (!isNaN(orderId) && orderId !== '') {
                getWebNumber(orderId)
                    .then(result => {
                        input.textContent = `# ${result}`;
                        input.style.display = 'block';
                    })
                    .catch(error => console.error(`Failed to fetch web order number for ${orderId}:`, error));
            }
        }
    });
}

function swapOrderForH4(elementToUpdate) {
    elementToUpdate.style.display = 'none';
    let orderId = elementToUpdate.textContent.trim().substring(7, elementToUpdate.length - 1);
    getWebNumber(orderId)
        .then(result => {
            elementToUpdate.textContent = `Order # ${result}`;
            elementToUpdate.style.display = 'table-cell';
        })
        .catch(error => console.error(`Failed to fetch web order number for ${orderId}:`, error));
}

function insertNewH4SameLevel(referenceElement, textContent, nodes) {
    if (!nodes.getElementById('order-detail-id')) {
        const newH5 = document.createElement('h5');
        newH5.style.webkitFontSmoothing = 'antialiased';
        newH5.style.textSizeAdjust = '100%';
        newH5.style.boxSizing = 'inherit';
        newH5.style.margin = '0px';
        newH5.style.fontFamily = 'Roboto, Helvetica, Arial, sans-serif';
        newH5.style.fontWeight = '400';
        newH5.style.fontSize = '2.125rem';
        newH5.style.lineHeight = '1.235';
        newH5.style.letterSpacing = '0.00735em';
        newH5.style.color = 'rgba(0, 0, 0, 0.87)';
        newH5.style.display = 'block';
        newH5.textContent = textContent;
        newH5.id = 'order-detail-id';
        referenceElement.insertAdjacentElement('afterend', newH5);
    } else {
        let orderH5Update = nodes.getElementById('order-detail-id');
        orderH5Update.textContent = textContent;
    }
}

function modifyH4OrderId(nodes) {
    const input = nodes.querySelector("div.MuiGrid-root.MuiGrid-item.MuiGrid-grid-xs-8.css-9zp9ag > h4");
    let input2 = nodes.querySelector("div.MuiGrid-root.MuiGrid-item.MuiGrid-grid-xs-8.css-9zp9ag > h4");
    if (input) {
        input.style.display = 'none';
        input2.id = "orderId-reference";
        let content = input.textContent.trim();
        if (content.length < 9) {
            setTimeout(() => modifyH4OrderId(nodes), 1000);
        } else {
            if (content.length > 9) {
                let contentOrderId = content.substring(7, content.length - 1);
                if (!isNaN(contentOrderId) && contentOrderId !== '') {
                    getWebNumber(contentOrderId)
                        .then(result => {
                            insertNewH4SameLevel(input, `Order #${result}`, nodes);
                        })
                        .catch(error => console.error(`Failed to fetch web order number for ${contentOrderId}:`, error));
                }
            }
        }
    }
}

export function setupObserver(iframeDocument) {
    if (parseBoolean(window.BC_CONTEXT?.disableAccountPortalObserver)) {
        return;
    }

    const enable_login_password_reset_message = parseBoolean(window.BC_CONTEXT?.enableLoginPasswordReset);
    const observer = new MutationObserver((mutationsList) => {
        const MERCHANT_ID = '0200c1d65daf40deb36843f73924f3f3';
        for (let mutation of mutationsList) {
            const subManager = iframeDocument.querySelector('#subscriptionManagerNav');
            if (subManager != null) {
                if (window.og?.msi) {
                    window.og.msi.reset();
                } else {
                    const checkInterval = setInterval(() => {
                        if (window.og?.msi) {
                            setLoading(false);
                            clearInterval(checkInterval);
                        }
                    }, 100);

                    const timeout = setTimeout(() => {
                        clearInterval(checkInterval);
                        console.error('OrderGroove subscription manager script failed to load');
                    }, 10000);

                    return () => {
                        clearInterval(checkInterval);
                        clearTimeout(timeout);
                    };

                    const script = document.createElement('script');
                    script.src = `https://staging.static.ordergroove.com/${MERCHANT_ID}/msi.js`;
                    script.async = true;
                    script.onload = () => setLoading(false);
                    script.onerror = () => {
                        console.error('Failed to load OrderGroove subscription manager script');
                        setLoading(false);
                    };
                    document.body.appendChild(script);

                    return () => {
                        if (document.body.contains(script)) {
                            document.body.removeChild(script);
                        }
                    };
                }
            }
            if (mutation.type === 'childList') {
                mutation.addedNodes.forEach(node => {
                    console.log("SEEING IS NOT BELIEVEING");
                    console.log(node);

                    if (node) {
                        let checkin = node.childNodes;
                        if (checkin.length > 0) {
                            let nodeTest = node.querySelectorAll('[data-testid="tableBody-orderId"]');
                            if (nodeTest && nodeTest.length > 0) {
                                modifyFormElements(iframeDocument);
                            }
                            let nodeTest2 = node.querySelectorAll("div.MuiGrid-root.MuiGrid-item.MuiGrid-grid-xs-8.css-9zp9ag > h4");
                            if (nodeTest2 && nodeTest2.length > 0) {
                                let nodeOrderDetailHeader = nodeTest2[0];
                                if (nodeOrderDetailHeader) {
                                    modifyH4OrderId(iframeDocument);
                                }
                            }
                        }
                        if (node.classList && node.classList.contains("css-19n8dai")) {
                            console.log("I am here targeting the page lists");
                        }
                        if (node.classList && node.classList.contains("css-7forzp")) {
                            // intentionally empty
                        }
                        if (node.classList && node.classList.contains("css-w0pj6f")) {
                            let signInForm = node.closest("form");
                            console.log(signInForm);
                            if (signInForm != null) {
                                var grandparentFormItem = signInForm.parentElement.parentElement;
                                if (enable_login_password_reset_message) {
                                    var loginMessageInfo = document.getElementById("loginMessage");
                                    if (loginMessageInfo != null) {
                                        grandparentFormItem.prepend(loginMessageInfo);
                                    }
                                }
                            }

                            var navigationButton = node.parentElement;
                            if (navigationButton.tagName === "BUTTON") {
                                let svgNextElement = navigationButton.querySelector('[data-testid="NavigateNextIcon"]');

                                if (svgNextElement) {
                                    const parentElement = navigationButton;
                                    if (!parentElement.hasAttribute('data-listener-attached')) {
                                        parentElement.addEventListener('click', function (event) {
                                            setTimeout(() => modifyH4OrderId(iframeDocument), 1000);
                                        });
                                        parentElement.setAttribute('data-listener-attached', 'true');
                                        const grandParentElement = parentElement.parentElement;
                                        let svgPrevElement = grandParentElement.querySelector('[data-testid="NavigateBeforeIcon"]');
                                        if (svgPrevElement) {
                                            const svgPrevParent = svgPrevElement.parentElement;
                                            if (!svgPrevParent.hasAttribute('data-listener-attached')) {
                                                svgPrevParent.addEventListener('click', function (event) {
                                                    setTimeout(() => modifyH4OrderId(iframeDocument), 1000);
                                                });
                                                svgPrevParent.setAttribute('data-listener-attached', 'true');
                                            }
                                        }
                                    }
                                }

                                if (navigationButton.textContent === 'SIGN IN') {
                                    console.log("Sign Clicked");

                                    var enableExternalLogin = window.BC_CONTEXT?.enableExternalLogin;
                                    console.log("enableExternalLogin" + enableExternalLogin);
                                    if (enableExternalLogin == true || enableExternalLogin === 'true') {
                                        var processedEvent = false;
                                        let existingForm = navigationButton.closest("form");

                                        existingForm.addEventListener('submit', function(e) {
                                            setTimeout(function() {
                                                processedEvent = false;
                                            }, 0);
                                        });

                                        existingForm.addEventListener('submit', (event) => {
                                            var divInputContainer2 = document.getElementById("divInputContainer");
                                            divInputContainer2.style.display = 'flex';
                                            var divInputResponse2 = document.getElementById("divInputResponse");
                                            divInputResponse2.style.display = 'none';

                                            let storedEvent = event;
                                            console.log("This Form Submitted");
                                            if (processedEvent == true) {
                                                return;
                                            }
                                            event.preventDefault();
                                            event.stopPropagation();
                                            console.log('Form submitted!');

                                            var onlineServicesHost1 = window.BC_CONTEXT?.onlineServices || '';
                                            let loginHost = onlineServicesHost1;
                                            let loginHostPath = "customers/CustomerLogin/Login";
                                            var currentStoreContext = window.BC_CONTEXT?.storeHash || '';

                                            let customerLoginUsernameElement = existingForm.querySelector("input[name='emailAddress']");
                                            if (customerLoginUsernameElement == null) {
                                                customerLoginUsernameElement = existingForm.querySelector("input[name='email']");
                                            }

                                            let customerLoginUsername = customerLoginUsernameElement.value;
                                            let customerLoginPasswordElement = existingForm.querySelector("input[name='password']");
                                            let customerLoginPassword = customerLoginPasswordElement.value;
                                            let loginRequestInfo = new Object();
                                            loginRequestInfo.Username = customerLoginUsername;
                                            loginRequestInfo.Password = customerLoginPassword;
                                            loginRequestInfo.StoreId = currentStoreContext;
                                            console.log(loginRequestInfo);

                                            const loginRequest = new Request(loginHost + loginHostPath, {
                                                headers: {
                                                    'Accept': 'application/json',
                                                    'Content-Type': 'application/json'
                                                },
                                                method: "POST",
                                                body: JSON.stringify(loginRequestInfo),
                                            });

                                            (async() => {
                                                try {
                                                    const response = await fetch(loginRequest);
                                                    if (!response.ok) {
                                                        // non-ok response handled below
                                                    }
                                                    const loginResult = await response.json();
                                                    console.log(loginResult);
                                                    if (loginResult.Success) {
                                                        processedEvent = true;
                                                        storedEvent.target.dispatchEvent(new Event(storedEvent.type, storedEvent));
                                                    } else {
                                                        let continueNatural = true;
                                                        if (loginResult.Message != null) {
                                                            if (loginResult.Message.includes("Password does not meet new requirements")) {
                                                                continueNatural = false;
                                                                var divUpdatePassword = document.getElementById("divUpdatePassword");
                                                                divUpdatePassword.style.display = 'flex';
                                                                var btnUpdatePassword = document.getElementById("btnUpdatePassword");
                                                                console.log(btnUpdatePassword);

                                                                let txtNewPassword = document.getElementById("txtNewPassword");
                                                                let txtConfirmNewPassword = document.getElementById("txtConfirmNewPassword");
                                                                let divformErrorMessage = document.getElementById("divform-error-message");

                                                                btnUpdatePassword.addEventListener('click', function() {
                                                                    divformErrorMessage.style.display = "none";
                                                                    let isPasswordSame = txtNewPassword.value == txtConfirmNewPassword.value ? true : false;
                                                                    let isValidPwd = isValidLoginPassword(txtNewPassword.value);

                                                                    if (isValidPwd && isPasswordSame) {
                                                                        let newPassword2 = txtNewPassword.value;
                                                                        let changePasswordRequestInfo = new Object();
                                                                        changePasswordRequestInfo.Username = customerLoginUsername;
                                                                        changePasswordRequestInfo.Password = customerLoginPassword;
                                                                        changePasswordRequestInfo.StoreId = currentStoreContext;
                                                                        changePasswordRequestInfo.NewPassword = newPassword2;
                                                                        console.log(changePasswordRequestInfo);

                                                                        let changePasswordServicePath = "customers/CustomerLogin/ChangePassword";
                                                                        const changePasswordRequest = new Request(loginHost + changePasswordServicePath, {
                                                                            headers: {
                                                                                'Accept': 'application/json',
                                                                                'Content-Type': 'application/json'
                                                                            },
                                                                            method: "POST",
                                                                            body: JSON.stringify(changePasswordRequestInfo),
                                                                        });

                                                                        fetch(changePasswordRequest)
                                                                            .then(changePasswordResponse => {
                                                                                console.log("Were Are Here");
                                                                                if (!changePasswordResponse.ok) {
                                                                                    // non-ok handled elsewhere
                                                                                }
                                                                                console.log("Making It Here2");
                                                                                const changePasswordResult = changePasswordResponse.json();
                                                                                console.log("Making It Here3");
                                                                                console.log(changePasswordResult);
                                                                                customerLoginPasswordElement.value = newPassword2;
                                                                                document.getElementById("divInputContainer").style.display = "none";
                                                                                document.getElementById("divInputResponse").style.display = "block";
                                                                            });
                                                                    } else {
                                                                        if (!isPasswordSame) {
                                                                            divformErrorMessage.innerText = "Your password do not match. Please enter the same password in both text boxes.";
                                                                        } else {
                                                                            divformErrorMessage.innerText = "The password provided does not meet our password requirements. Please enter a password at least 7 characters long and include at least 1 number and 1 letter.";
                                                                        }
                                                                        divformErrorMessage.style.display = "block";
                                                                    }
                                                                });

                                                                txtNewPassword.addEventListener('focus', function() {
                                                                    divformErrorMessage.classList.add("fade-out");
                                                                    setTimeout(() => {
                                                                        divformErrorMessage.style.display = "none";
                                                                        divformErrorMessage.classList.remove("fade-out");
                                                                    }, 2000);
                                                                });
                                                                txtConfirmNewPassword.addEventListener('focus', function() {
                                                                    divformErrorMessage.classList.add("fade-out");
                                                                    setTimeout(() => {
                                                                        divformErrorMessage.style.display = "none";
                                                                        divformErrorMessage.classList.remove("fade-out");
                                                                    }, 2000);
                                                                });
                                                            }
                                                        }
                                                        if (continueNatural) {
                                                            processedEvent = true;
                                                            storedEvent.target.dispatchEvent(new Event(storedEvent.type, storedEvent));
                                                        }
                                                    }
                                                } catch (error) {
                                                    console.error(error.message);
                                                    processedEvent = true;
                                                    storedEvent.target.dispatchEvent(new Event(storedEvent.type, storedEvent));
                                                }
                                            })();
                                        }, true);
                                    }

                                    // onlineService "Login" event moved to assets/js/theme/auth.js,
                                    // alongside the GA4 fireLogin() hook on the native login form.
                                }

                                if (navigationButton.textContent === 'Reset Password') {
                                    console.log("Found Reset Password");
                                    navigationButton.addEventListener('click', function(e) {
                                        console.log("click Forgot Password Button");
                                    });
                                }
                            }
                        }

                        if (node.classList && node.classList.contains("css-isbt42")) {
                            modifyMobileOrderElements(node);
                        }
                        if (node.classList && node.classList.contains("css-15j76c0")) {
                            modifyMobileOrderElements(node);
                        }
                        if (node.classList && node.classList.contains("css-1ei397r")) {
                            console.log("Trigger forgot Password");
                        }
                    }
                    if (node.tagName === 'FORM') {
                        modifyFormElements(iframeDocument);
                    }
                });
            }
        }
    });
    observer.observe(iframeDocument.body, {
        childList: true,
        subtree: true,
    });
}

function swapOrderId(elementToUpdate) {
    elementToUpdate.style.display = 'none';
    const orderId = elementToUpdate.textContent;
    getWebNumber(orderId)
        .then(result => {
            elementToUpdate.textContent = ` ${result}`;
            elementToUpdate.style.display = 'table-cell';
        })
        .catch(error => {
            console.error(`Failed to fetch web order number for ${orderId}:`, error);
            elementToUpdate.textContent = ` ${orderId} (Web # Error)`;
            elementToUpdate.style.display = 'table-cell';
        });
}

export function getWebNumber(orderIdToChange) {
    const osOrderClient = window.BC_CONTEXT?.onlineServices || '';
    const orders_route = window.BC_CONTEXT?.ordersRoute || '';
    const store_hash = window.BC_CONTEXT?.storeHash || '';
    const url = `${osOrderClient}${orders_route}OrderClient/webordernumberencode?orderId=${orderIdToChange}&siteName&appId&storeHash=${store_hash}`;
    const requestOptions = {
        method: "GET",
        mode: "cors",
        redirect: "follow"
    };

    return fetch(url, requestOptions)
        .then((response) => {
            if (!response.ok) {
                const message = `HTTP error! status: ${response.status} for orderId: ${orderIdToChange}`;
                console.error('Error fetching web order number:', message, response);
                throw new Error(message);
            }
            return response.text();
        })
        .catch((error) => {
            console.error('Error fetching web order number:', error, `orderId: ${orderIdToChange}`);
            throw error;
        });
}

function getOrderInfo(tableRow) {
    if (!window.BC_CONTEXT?.enableOrderDateLookup) {
        return;
    }

    let tableDataOrderId = tableRow.querySelector('[data-testid="tableBody-orderId"]');
    const bigOrderId = tableDataOrderId.textContent.trim();

    const store_hash = window.BC_CONTEXT?.storeHash || '';
    let orderInfoRequest = { "StoreId": store_hash, "OrderId": bigOrderId };

    const osOrderClient = window.BC_CONTEXT?.onlineServices || '';
    const orders_route = window.BC_CONTEXT?.ordersRoute || '';
    const url = `${osOrderClient}${orders_route}CustomerOrderManagementClient/GetOrderDate`;

    fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderInfoRequest)
    })
        .then(response => {
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            return response.json();
        })
        .then(data => {
            let orderCreatedDate = data.Result.date_created;
            console.log("orderCreatedDate:  " + orderCreatedDate);
            const thisCreatedDate = new Date(orderCreatedDate);
            let tableDataForCreatedDate = tableRow.querySelector('[data-testid="tableBody-createdAt"]');
            tableDataForCreatedDate.textContent = thisCreatedDate.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
        })
        .catch(error => {
            console.error('Error:', error);
        });
}

/**
 * Initialize the account portal iframe observer.
 * Replaces the DOMContentLoaded listener that was inline in account-iframe.html.
 * Must be called from global.js onReady() (which fires on DOMContentLoaded).
 */
export function initAccountIframe() {
    // Set up a "ready" watcher for dynamically-rendered elements
    var listeners = [],
        doc = window.document,
        MutationObserverCtor = window.MutationObserver || window.WebKitMutationObserver,
        readyObserver;

    function ready(selector, fn) {
        listeners.push({ selector, fn });
        if (!readyObserver) {
            readyObserver = new MutationObserverCtor(check);
            readyObserver.observe(doc.documentElement, { childList: true, subtree: true });
        }
        check();
    }

    function check() {
        for (var i = 0, len = listeners.length, listener, elements; i < len; i++) {
            listener = listeners[i];
            elements = doc.querySelectorAll(listener.selector);
            for (var j = 0, jLen = elements.length, element; j < jLen; j++) {
                element = elements[j];
                if (!element.ready) {
                    element.ready = true;
                    listener.fn.call(element, element);
                }
            }
        }
    }

    // Expose ready globally for any legacy callers
    window.ready = ready;

    function daIframe() {
        const iframe = document.querySelector("#bundle-container > div.bundle-app > iframe");
        if (!iframe) return;
        iframe.addEventListener('load', () => {
            const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
            modifyFormElements(iframeDocument);
            setupObserver(iframeDocument);
        });
    }

    window.daIframe = daIframe;

    ready("#bundle-container > div.MuiBox-root.css-1osrsoi > div > div", function(element) {
        daIframe();
    });

    // The buyer portal mounts its iframe on every page without a title (Lighthouse/axe frame-title).
    ready("#bundle-container > div.bundle-app > iframe", function(iframe) {
        if (!iframe.hasAttribute('title')) iframe.setAttribute('title', 'Account portal');
    });
}
